import * as THREE from 'three';

/**
 * Aerial perspective at the scale of a garden.
 *
 * ## The problem it exists to solve
 *
 * The scene already has haze — `SkyEnvironment` puts a `FogExp2` on it — but that fog is tuned for
 * the *backdrop*, and three's exp² curve has exactly one length scale. At the density that keeps a
 * template's distant terrain from sitting at the same contrast as the site, the numbers inside the
 * site itself come out like this:
 *
 * | 10 m | 20 m | 30 m | 100 m | 300 m |
 * |------|------|------|-------|-------|
 * | 0.03% | 0.10% | 0.23% | 2.5% | 21% |
 *
 * Across the whole range a garden actually occupies — call it 3 m to 30 m — the fog varies by two
 * tenths of one percent. It is doing nothing at all until about 150 m, by which point you are
 * looking at scenery rather than at design. So every plant from the front of the bed to the back
 * fence is drawn at identical contrast, identical saturation, and identically black shadows, and the
 * frame reads as an elevation drawing rather than as a photograph.
 *
 * Raising `FOG_DENSITY` cannot fix it. To get eight percent of haze at 20 m the density has to go to
 * about 0.014, which erases everything past 100 m. The scene has two length scales and one
 * exponential cannot serve both, so the near one gets a term of its own. `SkyEnvironment` goes on
 * owning the backdrop; this owns the 4–34 m band and nothing else.
 *
 * ## What it does
 *
 * Two things, in the order air does them. Distance eats chroma first, so the far end of the ramp
 * loses a sixth of its saturation. Then in-scattered light fills the shadows — which is the half
 * that matters most here, because a yew at 30 m currently has shadows as black as one at 3 m, and
 * nothing says "this is a diagram" more plainly than a distant shadow with no air in it.
 *
 * Deliberately *not* a foreground brightness lift, which is the intuitive version of the same idea
 * and the weak one: straight gain on near geometry blows out sunlit foliage past about four percent
 * and reads as washed rather than as close. Pushing the far end back is the same separation bought
 * without spending any headroom.
 *
 * ## Why it is patched into three's own chunk
 *
 * The grade has to reach *everything that is scenery* — terrain, flora, site objects, fauna,
 * staffage, whatever a GLTF prop brought its own materials for. Grading only the surfaces this
 * codebase builds materials for would leave an imported tree ungraded beside a generated one, and a
 * seam like that is worse than the flatness it was meant to fix. There is no single funnel every
 * material passes through, so instead of wiring five owners and missing the sixth, the fog chunk
 * itself is rewritten once, before anything compiles.
 *
 * That choice pays for itself three times over:
 *
 * - `vFogDepth` already exists and is already interpolated, so the grade costs no new varying.
 * - `fogColor` is already refreshed per material per frame from `scene.fog.color`, which
 *   `SkyEnvironment.update` keeps on the hour — so the haze can never disagree with the sky about
 *   what time it is, without needing a uniform of its own.
 * - `#ifdef USE_FOG` is already the opt-out. `CapturePasses` nulls `scene.fog` for the semantic
 *   passes, which drops the define, which drops the grade — and a species-ID map must not be graded.
 *   That was free.
 *
 * The cost is that anything which is *not* scenery has to say so, which is what
 * {@link exemptOverlays} is for.
 *
 * ## Colour space
 *
 * `fog_fragment` runs after `tonemapping_fragment` and after `colorspace_fragment`, so this is a
 * display-space grade. That is the right place for it — contrast, saturation and shadow lift are
 * display-space operations in every grading tool there is, and running before ACES would make the
 * lift fight the tone curve instead of riding on it.
 *
 * `fogColor` arrives in whatever space `gl_FragColor` is in: three converts it with
 * `getUnlitUniformColorSpace`, which follows the same render-target rule the output conversion does.
 * So the mix is always consistent. The *luma weights* are not equally portable — three writes linear
 * when rendering into a target, and a linear mid-shadow has a much lower luma than the same shadow
 * encoded — so a photo capture gets a slightly stronger lift than the viewport does. It is a few
 * percent on the darkest values and nothing shifts hue, which is why it is written down here rather
 * than corrected with machinery.
 */

/**
 * The ramp, in metres of view depth.
 *
 * Fixed distances, and not something scaled by how far the camera has orbited out. Haze is a
 * property of the air between here and there, so a bed 20 m away has to look the same whether it is
 * the subject of the frame or the edge of one. Tying the ramp to camera distance is the obvious next
 * idea and it makes the entire scene breathe every time anyone zooms.
 *
 * The consequence is that a whole-site view from 200 m sits past the end of the ramp and is graded
 * flat, with the `FogExp2` supplying what gradient there is. That is correct: at 200 m the site
 * *is* the backdrop.
 *
 * The near end starts at 4 m rather than at 0 so a plant being placed under the cursor carries no
 * grade at all, and the far end sits in the same family as the detail fades in `DetailShading` —
 * 40 m for a plant, 55 m for the ground — because they are describing the same distance at which a
 * garden stops being made of individual plants.
 */
export const GRADE_NEAR_METERS = 4;
export const GRADE_FAR_METERS = 34;

/** Chroma lost at the far end of the ramp. */
const DESATURATION = 0.16;

/** How far the far end is pulled toward the horizon colour, at a value of pure black. */
const SHADOW_LIFT = 0.2;

/**
 * How much of the lift a full-white value is spared.
 *
 * In-scattered light is *added* along the path, so it is the values with room to move that move: a
 * black cannot stay black behind thirty metres of air, while a sunlit leaf already sits near the
 * haze's own brightness and barely shifts. A plain mix says some of that; weighting it by luma says
 * it properly, and is what lets the lift be large enough to see without turning distant sunlit
 * foliage milky. At 0.75 a black at the far end moves 20% toward the horizon and a white moves 5%.
 */
const HIGHLIGHT_HOLD = 0.75;

/**
 * The line in three's `fog_fragment` that the grade is appended to.
 *
 * Named here so the upgrade test and the patch cannot disagree about it. Appending *after* three's
 * own mix rather than replacing it is deliberate: the backdrop fog stays exactly as it was and this
 * lands on top of it.
 */
export const DEPTH_GRADE_ANCHOR = 'gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fogFactor );';

/** Six decimal places and always a point, so every emitted literal is a valid GLSL float. */
function glsl(value: number): string {
  const text = value.toFixed(6).replace(/0+$/, '');
  return text.endsWith('.') ? `${text}0` : text;
}

/** The grade itself, as it is spliced into the fog chunk. Pure, so it can be read in a test. */
export function depthGradeGlsl(): string {
  return `
	// --- depth grade: see src/rendering/DepthGrade.ts ---
	{

		float lgGradeDepth = smoothstep( ${glsl(GRADE_NEAR_METERS)}, ${glsl(GRADE_FAR_METERS)}, vFogDepth );

		float lgGradeLuma = dot( gl_FragColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );

		// Chroma first, because distance takes it first.
		gl_FragColor.rgb = mix( vec3( lgGradeLuma ), gl_FragColor.rgb, 1.0 - lgGradeDepth * ${glsl(DESATURATION)} );

		// Then the shadows. lgGradeLuma is still exact here: a mix toward vec3( luma ) preserves luma
		// by construction, so the line above cannot have moved it.
		gl_FragColor.rgb = mix(
			gl_FragColor.rgb,
			fogColor,
			lgGradeDepth * ${glsl(SHADOW_LIFT)} * ( 1.0 - ${glsl(HIGHLIGHT_HOLD)} * lgGradeLuma )
		);

	}
`;
}

/** Splice the grade into a fog chunk. Pure — {@link installDepthGrade} is what makes it take effect. */
export function gradeFogChunk(chunk: string): string {
  return chunk.replace(DEPTH_GRADE_ANCHOR, `${DEPTH_GRADE_ANCHOR}\n${depthGradeGlsl()}`);
}

let installed = false;

/**
 * Rewrite `THREE.ShaderChunk.fog_fragment` so every fogged material in the scene carries the grade.
 *
 * Must be called before the first material compiles — `WebGLProgram` resolves `#include` from
 * `ShaderChunk` at compile time and caches the program, so anything already built keeps the shader it
 * was built with. Idempotent, and a no-op if the anchor is gone: a three upgrade that renames the
 * line should leave the scene rendering exactly as it does today and fail a test, not throw at boot.
 *
 * Returns whether the grade is in force.
 */
export function installDepthGrade(): boolean {
  if (installed) return true;

  const stock = THREE.ShaderChunk.fog_fragment;
  if (stock.split(DEPTH_GRADE_ANCHOR).length - 1 !== 1) return false;

  THREE.ShaderChunk.fog_fragment = gradeFogChunk(stock);
  installed = true;
  return true;
}

/**
 * Take a subtree out of the air.
 *
 * Overlays are UI, not scenery: a selection box, a brush ghost, a north arrow and a contour label are
 * drawn in world space but they are *readings of* the garden rather than things in it, and they have
 * to keep the colour they were given at any distance. That is true independently of this module — an
 * overlay had no business fogging in the first place — but the grade is what makes it visible, since
 * a yellow selection box around a plant on the far side of a large site would otherwise arrive
 * noticeably pale.
 *
 * `fog = false` is the opt-out three already provides, and dropping `USE_FOG` takes the grade with
 * it.
 *
 * Applied as a sweep rather than at each material's constructor because `overlayRoot` is filled
 * lazily by a dozen different tools — selection helpers appear per selection, tag markers per tag,
 * ghosts per stroke — and a rule spread across twelve files is a rule the thirteenth tool will not
 * know about. The sweep is idempotent and, after the first pass over a given material, free: the
 * guard in {@link unfog} is what makes calling it every frame cost a traversal of a few dozen objects
 * and nothing else.
 */
export function exemptOverlays(root: THREE.Object3D): void {
  root.traverse((object) => {
    const material = (object as Partial<THREE.Mesh>).material;
    if (!material) return;
    if (Array.isArray(material)) {
      for (const entry of material) unfog(entry);
    } else {
      unfog(material);
    }
  });
}

function unfog(material: THREE.Material): void {
  // `fog` lives on the concrete materials that support it rather than on the base type, and a
  // material that never had it (`MeshDepthMaterial`, say) is already exempt.
  const fogged = material as THREE.Material & { fog?: boolean };
  if (fogged.fog !== true) return;
  fogged.fog = false;
  // The flag is a shader define, so the program has to be rebuilt. The guard above is what keeps
  // that to once per material instead of once per frame.
  material.needsUpdate = true;
}
